/**
 * Agent CSV import — pure client-side parser. Each parsed row is uploaded
 * individually via `useUpsertAgentWithEmail`; there is no backend preview/commit
 * endpoint. Header order in the template is fixed: email, full name, WhatsApp, memo.
 */
import { parsePhoneNumberFromString } from 'libphonenumber-js';
const TEMPLATE_HEADERS = ['email', 'full name', 'WhatsApp', 'memo'];
const TEMPLATE_EXAMPLE_ROW = ['agent@example.com', 'Agent Example', '+12025550123', 'Example row — replace or delete'];
const HEADER_ALIASES = {
    email: 'email',
    'full name': 'name',
    whatsapp: 'whatsAppE164',
    memo: 'adminNotes',
};
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/**
 * RFC-4180-lite parser: handles quoted fields, embedded commas, escaped
 * double-quotes (""), and both CRLF and LF line endings.
 */
function parseCsvText(text) {
    const rows = [];
    let current = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i++;
                }
                else {
                    inQuotes = false;
                }
            }
            else {
                field += ch;
            }
            continue;
        }
        if (ch === '"') {
            inQuotes = true;
        }
        else if (ch === ',') {
            current.push(field);
            field = '';
        }
        else if (ch === '\n' || ch === '\r') {
            current.push(field);
            field = '';
            rows.push(current);
            current = [];
            if (ch === '\r' && text[i + 1] === '\n')
                i++;
        }
        else {
            field += ch;
        }
    }
    // Flush trailing field/row when the file doesn't end with a newline.
    if (field.length > 0 || current.length > 0) {
        current.push(field);
        rows.push(current);
    }
    // Drop fully-empty trailing rows (e.g. blank line at EOF).
    return rows.filter((row) => row.some((cell) => cell.trim().length > 0));
}
export function parseAgentsCsv(text) {
    const rows = parseCsvText(text);
    if (rows.length === 0) {
        return { formatError: 'empty', validRows: [], invalidRows: [] };
    }
    const headerRow = rows[0].map((h) => h.trim().toLowerCase());
    // Reject files with any header that isn't part of the template. Empty header
    // cells (from a trailing comma) are tolerated since they don't change semantics.
    for (const h of headerRow) {
        if (h.length > 0 && !(h in HEADER_ALIASES)) {
            return { formatError: 'unknown-columns', validRows: [], invalidRows: [] };
        }
    }
    const columnMap = headerRow.map((h) => HEADER_ALIASES[h] ?? null);
    if (!columnMap.includes('email')) {
        return { formatError: 'missing-email-column', validRows: [], invalidRows: [] };
    }
    const validRows = [];
    const invalidRows = [];
    for (let i = 1; i < rows.length; i++) {
        const cells = rows[i];
        const row = {};
        for (let c = 0; c < cells.length; c++) {
            const key = columnMap[c];
            if (!key)
                continue;
            const value = cells[c]?.trim() ?? '';
            if (value.length === 0)
                continue;
            row[key] = value;
        }
        const email = row.email;
        if (!email || !EMAIL_RE.test(email)) {
            invalidRows.push({ row: i + 1, message: 'Invalid or missing email' });
            continue;
        }
        let whatsAppE164 = row.whatsAppE164;
        if (whatsAppE164) {
            const candidate = whatsAppE164.startsWith('+') ? whatsAppE164 : `+${whatsAppE164}`;
            const parsed = parsePhoneNumberFromString(candidate);
            if (!parsed || !parsed.isValid()) {
                invalidRows.push({ row: i + 1, message: 'Invalid WhatsApp number' });
                continue;
            }
            whatsAppE164 = parsed.format('E.164');
        }
        validRows.push({
            email,
            name: row.name,
            whatsAppE164,
            adminNotes: row.adminNotes,
        });
    }
    return { formatError: null, validRows, invalidRows };
}
export async function readFileText(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
        reader.readAsText(file);
    });
}
function csvEscape(value) {
    return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}
export function downloadAgentsCsvTemplate() {
    if (typeof window === 'undefined')
        return;
    const csv = `${TEMPLATE_HEADERS.join(',')}\n${TEMPLATE_EXAMPLE_ROW.map(csvEscape).join(',')}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'agents-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
